import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Users,
  RefreshCw,
  Download,
  FileText,
  Mail,
  Phone,
  Calendar,
  MessageSquare,
  Loader2,
  Filter,
  Briefcase,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import NotificationEmailsPanel from './NotificationEmailsPanel';
import {
  applicationsApi,
  STATUS_LABELS,
  STATUS_COLORS,
  POST_LABELS,
} from './applicationsApi';

const KpiCard = ({ label, value, tone, testid }) => {
  const tones = {
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    brown: 'bg-[#F5EDE4] text-[#5D4E37] border-[#C9A086]',
  };
  return (
    <div className={`p-3 rounded-lg border ${tones[tone] || tones.slate}`} data-testid={testid}>
      <div className="text-xs uppercase font-medium tracking-wider opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
};

const ApplicationsTab = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        applicationsApi.list(filter || undefined),
        applicationsApi.stats(),
      ]);
      setItems(listRes.items || []);
      setStats(statsRes);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement candidatures');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (a) =>
        a.full_name.toLowerCase().includes(q) ||
        (a.email || '').toLowerCase().includes(q) ||
        (a.phone || '').toLowerCase().includes(q) ||
        (a.job_slug || '').toLowerCase().includes(q),
    );
  }, [items, search]);

  const openDetail = (app) => {
    setDetail(app);
    setNoteDraft(app.admin_note || '');
  };

  const closeDetail = () => {
    setDetail(null);
    setNoteDraft('');
  };

  const patch = async (payload) => {
    if (!detail) return;
    setSaving(true);
    try {
      const updated = await applicationsApi.patch(detail.id, payload);
      setDetail(updated);
      setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
      toast.success('Candidature mise à jour');
      const statsRes = await applicationsApi.stats();
      setStats(statsRes);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const downloadFile = async (app, kind) => {
    try {
      const { blob } = await applicationsApi.downloadFile(app.id, kind);
      const ref = kind === 'cv' ? app.cv_file : app.cover_letter_file;
      const filename = ref?.original_filename || `${kind}-${app.id}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de télécharger le fichier');
    }
  };

  const doDelete = async () => {
    if (!detail) return;
    try {
      await applicationsApi.remove(detail.id);
      setItems((prev) => prev.filter((x) => x.id !== detail.id));
      toast.success('Candidature supprimée');
      setConfirmDelete(false);
      closeDetail();
      const statsRes = await applicationsApi.stats();
      setStats(statsRes);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    }
  };

  return (
    <div className="space-y-4" data-testid="applications-tab">
      {/* Configuration des destinataires des notifications */}
      <NotificationEmailsPanel />

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <KpiCard label="Total" value={stats.total} tone="brown" testid="kpi-total" />
          <KpiCard label="Nouveaux" value={stats.counts.nouveau} tone="blue" testid="kpi-nouveau" />
          <KpiCard label="À revoir" value={stats.counts.a_revoir} tone="amber" testid="kpi-a-revoir" />
          <KpiCard label="Retenus" value={stats.counts.retenu} tone="emerald" testid="kpi-retenu" />
          <KpiCard label="Refusés" value={stats.counts.refuse} tone="slate" testid="kpi-refuse" />
        </div>
      )}

      {/* Toolbar */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-[#5D4E37]">
            <Users className="w-5 h-5" />
            <span className="font-semibold">Candidatures reçues</span>
            <Badge variant="outline">{items.length}</Badge>
          </div>
          <div className="flex-1" />
          <input
            type="text"
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm min-w-[220px]"
            data-testid="applications-search"
          />
          <div className="flex items-center gap-1">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
              data-testid="applications-filter"
            >
              <option value="">Tous les statuts</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={refresh}
            disabled={loading}
            data-testid="applications-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </CardContent>
      </Card>

      {/* Liste */}
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement…
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="text-center py-14 text-slate-500">
            Aucune candidature à afficher.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-2">
          {filtered.map((a) => (
            <Card
              key={a.id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => openDetail(a)}
              data-testid={`application-row-${a.id}`}
            >
              <CardContent className="p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h4 className="font-semibold text-[#5D4E37]">{a.full_name}</h4>
                    <Badge className={STATUS_COLORS[a.status] || STATUS_COLORS.nouveau}>
                      {STATUS_LABELS[a.status] || a.status}
                    </Badge>
                    <Badge variant="outline">{POST_LABELS[a.post] || a.post}</Badge>
                    {a.job_slug && (
                      <Badge variant="outline" className="border-[#C9A086]">
                        <Briefcase className="w-3 h-3 mr-1" />
                        {a.job_slug}
                      </Badge>
                    )}
                    {a.cv_file && (
                      <Badge variant="outline" className="border-emerald-500 text-emerald-700">
                        <FileText className="w-3 h-3 mr-1" />
                        CV
                      </Badge>
                    )}
                    {a.cover_letter_file && (
                      <Badge variant="outline" className="border-emerald-500 text-emerald-700">
                        <FileText className="w-3 h-3 mr-1" />
                        Lettre
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-slate-600 flex flex-wrap gap-3">
                    <span className="inline-flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {a.phone}
                    </span>
                    {a.email && (
                      <span className="inline-flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {a.email}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-slate-400">
                      <Calendar className="w-3 h-3" />
                      {(a.created_at || '').slice(0, 10)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal détail */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && closeDetail()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="application-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex flex-wrap items-center gap-2">
                  {detail.full_name}
                  <Badge className={STATUS_COLORS[detail.status] || STATUS_COLORS.nouveau}>
                    {STATUS_LABELS[detail.status] || detail.status}
                  </Badge>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                {/* Infos contact */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-slate-400 uppercase">Téléphone</div>
                    <a href={`tel:${detail.phone}`} className="text-[#5D4E37] font-medium hover:underline">
                      {detail.phone}
                    </a>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 uppercase">Email</div>
                    {detail.email ? (
                      <a href={`mailto:${detail.email}`} className="text-[#5D4E37] font-medium hover:underline">
                        {detail.email}
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 uppercase">Poste souhaité</div>
                    <div className="text-[#5D4E37]">{POST_LABELS[detail.post] || detail.post}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 uppercase">Fiche liée</div>
                    <div className="text-[#5D4E37] font-mono text-xs">{detail.job_slug || '—'}</div>
                  </div>
                  <div className="sm:col-span-2">
                    <div className="text-xs text-slate-400 uppercase">Reçue le</div>
                    <div className="text-[#5D4E37]">
                      {(detail.created_at || '').slice(0, 16).replace('T', ' ')}
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <div className="text-xs text-slate-400 uppercase mb-1 flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" /> Message du candidat
                  </div>
                  <div className="bg-slate-50 border rounded-md px-3 py-2 text-sm whitespace-pre-wrap">
                    {detail.message}
                  </div>
                </div>

                {/* Fichiers */}
                <div className="flex flex-wrap gap-2">
                  {detail.cv_file && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadFile(detail, 'cv')}
                      data-testid="application-download-cv"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      CV ({Math.round((detail.cv_file.size || 0) / 1024)} Ko)
                    </Button>
                  )}
                  {detail.cover_letter_file && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadFile(detail, 'cover_letter')}
                      data-testid="application-download-cover"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Lettre de motivation
                    </Button>
                  )}
                  {detail.cv_link && (
                    <a href={detail.cv_link} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="outline">
                        <FileText className="w-4 h-4 mr-1" />
                        Lien externe
                      </Button>
                    </a>
                  )}
                  {!detail.cv_file && !detail.cover_letter_file && !detail.cv_link && (
                    <span className="text-xs text-slate-400 italic">Aucun document joint.</span>
                  )}
                </div>

                {/* Note admin */}
                <div>
                  <div className="text-xs text-slate-400 uppercase mb-1">Note interne</div>
                  <textarea
                    rows={3}
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onBlur={() => {
                      if ((detail.admin_note || '') !== noteDraft) {
                        patch({ admin_note: noteDraft });
                      }
                    }}
                    className="w-full border rounded-md px-3 py-2 text-sm resize-y"
                    placeholder="Impression, prochaines étapes…"
                    data-testid="application-admin-note"
                  />
                </div>

                {/* Actions statut */}
                <div className="flex flex-wrap gap-2 pt-2 border-t">
                  <span className="text-xs text-slate-400 self-center mr-2">Changer le statut :</span>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <Button
                      key={k}
                      size="sm"
                      variant={detail.status === k ? 'default' : 'outline'}
                      className={detail.status === k ? 'bg-[#5D4E37] hover:bg-[#4A3E2C]' : ''}
                      disabled={saving || detail.status === k}
                      onClick={() => patch({ status: k })}
                      data-testid={`application-status-${k}`}
                    >
                      {v}
                    </Button>
                  ))}
                  <div className="flex-1" />
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => setConfirmDelete(true)}
                    data-testid="application-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Supprimer
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette candidature ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive. Le CV et la lettre restent stockés mais ne
              seront plus accessibles depuis SADAdmin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="application-delete-confirm"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ApplicationsTab;
